import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiSave, FiCpu, FiAlertTriangle, FiCheckCircle, FiMail, FiBell, FiUserPlus } = FiIcons;

    const AutomationSettings = () => {
      const { appSettings, saveAppSettings } = useData();
      const [settings, setSettings] = useState({
        paymentReminders: { enabled: false, daysOverdue: 7 },
        sessionConfirmations: { enabled: false, hoursBefore: 24 },
        welcomeEmails: { enabled: false }
      });
      const [loading, setLoading] = useState(false);
      const [saveSuccess, setSaveSuccess] = useState(false);

      useEffect(() => {
        if (appSettings.automationSettings) {
          setSettings(appSettings.automationSettings);
        }
      }, [appSettings]);

      const handleChange = (category, field, value) => {
        setSettings(prev => ({ ...prev, [category]: { ...prev[category], [field]: value } }));
      };

      const handleSave = async () => {
        setLoading(true);
        setSaveSuccess(false);
        try {
          await saveAppSettings({ ...appSettings, automationSettings: settings });
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
          console.error('Error saving automation settings:', error);
          alert('Failed to save settings.');
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Automation Settings</h2>
              <p className="text-sm text-gray-600 mt-1">Configure automated actions to streamline your workflow</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiCpu} className="text-blue-500 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Backend Automation Required</h4>
                <p className="text-sm text-blue-800 mt-1">
                  These automations run on the server. To enable them, you must set up a scheduled job (like a cron job) to trigger the `runAutomations` Firebase Function periodically.
                  <strong>Refer to the `AUTOMATION_SETUP_GUIDE.md` file for instructions.</strong>
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="space-y-8 divide-y divide-gray-200">
              {/* Payment Reminders */}
              <div className="pt-8 first:pt-0">
                <div className="flex items-center gap-4 mb-4">
                  <SafeIcon icon={FiMail} className="text-2xl text-red-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Automatic Payment Reminders</h3>
                    <p className="text-sm text-gray-600">Send email reminders for overdue payments.</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={settings.paymentReminders.enabled} onChange={(e) => handleChange('paymentReminders', 'enabled', e.target.checked)} className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span className="text-sm font-medium text-gray-900">Enable Payment Reminders</span>
                  </label>
                  {settings.paymentReminders.enabled && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pl-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Send reminder when payment is overdue by:</label>
                      <div className="flex items-center gap-2">
                        <input type="number" value={settings.paymentReminders.daysOverdue} onChange={(e) => handleChange('paymentReminders', 'daysOverdue', parseInt(e.target.value) || 0)} className="w-24 px-3 py-2 border border-gray-300 rounded-lg" min="1" />
                        <span className="text-sm text-gray-700">days</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
              {/* Session Confirmations */}
              <div className="pt-8">
                <div className="flex items-center gap-4 mb-4">
                  <SafeIcon icon={FiBell} className="text-2xl text-blue-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Automatic Session Confirmations</h3>
                    <p className="text-sm text-gray-600">Send email confirmations before a scheduled session.</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={settings.sessionConfirmations.enabled} onChange={(e) => handleChange('sessionConfirmations', 'enabled', e.target.checked)} className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span className="text-sm font-medium text-gray-900">Enable Session Confirmations</span>
                  </label>
                  {settings.sessionConfirmations.enabled && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pl-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Send confirmation email:</label>
                      <div className="flex items-center gap-2">
                        <input type="number" value={settings.sessionConfirmations.hoursBefore} onChange={(e) => handleChange('sessionConfirmations', 'hoursBefore', parseInt(e.target.value) || 0)} className="w-24 px-3 py-2 border border-gray-300 rounded-lg" min="1" />
                        <span className="text-sm text-gray-700">hours before session</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
              {/* Welcome Emails */}
              <div className="pt-8">
                <div className="flex items-center gap-4 mb-4">
                  <SafeIcon icon={FiUserPlus} className="text-2xl text-green-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Automatic Welcome Emails</h3>
                    <p className="text-sm text-gray-600">Send a welcome email to newly added parents.</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={settings.welcomeEmails.enabled} onChange={(e) => handleChange('welcomeEmails', 'enabled', e.target.checked)} className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span className="text-sm font-medium text-gray-900">Enable Welcome Emails</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end items-center gap-4 mt-8 pt-6 border-t">
              {saveSuccess && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-2 text-green-600 text-sm">
                  <SafeIcon icon={FiCheckCircle} /> <span>Settings saved successfully!</span>
                </motion.div>
              )}
              <motion.button whileHover={{ scale: loading ? 1 : 1.05 }} whileTap={{ scale: loading ? 1 : 0.95 }} onClick={handleSave} disabled={loading} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>
                ) : (
                  <><SafeIcon icon={FiSave} /> Save Automation Settings</>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      );
    };
    export default AutomationSettings;